/**
 * Resolve the URL slugpath for a match.
 *
 * Each match arrives with `path` + `label` already decorated by
 * `getData()` (issue #37). This shim runs the path through SvelteKit's
 * `resolve(...)` per render so adapter-static's relative-path rewrite
 * stays correct.
 *
 * When the global `appState.embed` flag is set (i.e. we're rendering
 * inside the `/embeds/*` layout, which lives in a third-party iframe),
 * URLs are instead anchored on the package `homepage` so they always
 * point back at the live production page rather than at a SvelteKit
 * relative URL that would resolve against the host page.
 */

import { appState } from '$lib/stores/index.svelte';
import { homepage } from '$pkg';
import { resolve } from '$app/paths';

/**
 * Returns a `{ label, url }` link descriptor for a match page:
 *   • Both sides known → `{ url: '<base>/matches/<slug>/', label }`.
 *   • Only one side known → `{ url: '.', label }` (skip the link).
 *   • Neither side known → `undefined`.
 *
 * @param {Object} match - A match object decorated by `getData()`.
 * @returns {{ label: string, url: string } | undefined}
 */
export const getMatchPath = (match) => {
  if (!match?.path) return;

  // "TBD" matches (one side unknown) have no real page yet; keep the
  // upstream behaviour and skip the link regardless of `embed`.
  if (match.path === '.') {
    return { label: match.label ?? '', url: '.' };
  }

  if (appState.embed) {
    const base = homepage.replace(/\/$/, '');
    const tail = String(match.path).replace(/^\//, '');
    return {
      label: match.label ?? '',
      url: `${base}/${tail}`,
    };
  }

  return {
    label: match.label ?? '',
    url: resolve(`/${match.path}`),
  };
};

export default getMatchPath;
